"use client";

import { type SessionQueryParams } from "better-auth";
import { useRouter } from "next/navigation";

import { SignInComponent } from "@/components/auth/SignInComponent";
import { SignInModal } from "@/components/auth/SignInModal";
import { Spinner } from "@/components/ui/spinner";
import { useSession, type Session, type SessionUser } from "@/lib/auth/client";
import {
  buildDestinationFromCurrentLocation,
  buildSigninUrl,
} from "@/lib/deep-linking";

interface UseAuthOptions {
  /**
   * Whether authentication is required
   */
  requireAuth?: boolean;
  /**
   * Full destination URL (path + search + hash) for post-auth redirect
   * Uses type-safe deep linking (ADR-018)
   * If not provided, uses current location automatically
   * @example "/listing/abc123?ref=email#place-bid"
   */
  destination?: string;
}

interface UseAuthReturn {
  /**
   * Whether the user is authenticated
   */
  isAuthenticated: boolean;
  /**
   * Whether the session is loading
   */
  isLoading: boolean;
  /**
   * Whether the user is unauthenticated
   */
  isUnauthenticated: boolean;
  /**
   * Whether the user can access the requested resource
   */
  canAccess: boolean;
  /**
   * The current session ID (if available), convenience property
   */
  sessionId: string | null;
  /**
   * The current user data (Better Auth SessionUser - minimal fields only)
   */
  user: SessionUser | null;
  /**
   * The current session data
   */
  session: Session | null;
  /**
   * Function to render the login modal
   */
  renderLogin: (style: "modal" | "redirect" | "embedded") => React.ReactElement;
  /**
   * Function to refetch the session
   */
  refetch: (queryParams?: { query?: SessionQueryParams }) => void;
}

/**
 * Custom hook for handling authentication redirects using a modal instead of page redirects
 *
 * This hook replaces the pattern of redirecting to /signin
 * with showing a login modal instead.
 *
 * @param options Configuration options
 * @returns Authentication state and modal controls
 */
function useAuthWithRedirect({
  requireAuth = true,
  destination,
}: UseAuthOptions = {}): UseAuthReturn {
  const { data, isPending, refetch } = useSession();

  // Cache user/session in state for consistency
  // Use SessionUser type explicitly (Better Auth minimal session fields only)
  // Type assertion needed to satisfy TypeScript's strict type checking
  // const [userFromState, setUserFromState] = useState<SessionUser | null>(
  //   user as SessionUser | null,
  // );
  // const [sessionFromState, setSessionFromState] = useState<Session | null>(
  //   session as Session | null,
  // );
  // const [isPendingState, setIsPendingState] = useState(isPending);

  // Update cached state when Better Auth state changes
  // useEffect(() => {
  //   setUserFromState(user as SessionUser | null);
  //   setSessionFromState(session as Session | null);
  //   setIsPendingState(isPending);
  // }, [user, session, isPending]);

  const router = useRouter();

  // This blocks until the session is loaded
  const renderLogin = (
    style: "modal" | "redirect" | "embedded" = "embedded",
  ): React.ReactElement => {
    if (isPending) return <Spinner />;
    if (data?.user) {
      throw new Error("Already Authenticated! Don't render login.");
    }

    // Unauthenticated!
    switch (style) {
      case "modal":
        return <SignInModal open={true} destination={destination} />;
      case "redirect":
        router.push(
          buildSigninUrl(destination || buildDestinationFromCurrentLocation()),
        );
        throw new Error("Is routing async?");
      case "embedded":
        return <SignInComponent destination={destination} />;
    }
  };

  return {
    isLoading: isPending, //isPendingState,
    isUnauthenticated: !isPending && !data?.user,
    isAuthenticated: !!data?.user,
    canAccess: !requireAuth || (!isPending && !!data?.user), // auth is not required or not loading and is authenticated
    session: data ?? null, // sessionFromState, // always use the cached session
    user: data?.user ?? null, // userFromState, // Better Auth user object
    sessionId: data?.session.id ?? null, //userFromState?.id ?? null,
    renderLogin,
    refetch,
  };
}

/**
 * Hook for components that need to redirect unauthenticated users
 *
 * NOTE: Consider using <RequireAuth> guard component instead (ADR-003)
 *
 * @param destination Full destination URL (path + search + hash) for post-auth redirect
 * @returns Authentication state and redirect handler
 */
export function useRequireAuth(destination?: string) {
  return useAuthWithRedirect({
    requireAuth: true,
    destination,
  });
}

/**
 * Hook for components that need to check authentication but don't require it
 *
 * NOTE: Consider using Better Auth useAuth hook directly (lib/auth/client.ts)
 *
 * @param destination Full destination URL (path + search + hash) for post-auth redirect
 * @returns Authentication state and modal controls
 */
export function useOptionalAuth(destination?: string) {
  return useAuthWithRedirect({
    requireAuth: false,
    destination,
  });
}
